import { Component, OnInit, ViewChild } from '@angular/core';
import { ModalController, NavParams, IonInput, LoadingController, AlertController, Platform } from '@ionic/angular';
import { Router } from '@angular/router';
import { APIService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { ObserverService } from '../../services/observer.service';
import { CountryPickerPage } from './../../modals/country-picker/country-picker.page';
import { CountryService } from './../../services/country.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-organisation-shipping-address-add',
    templateUrl: './organisation-shipping-address-add.page.html',
    styleUrls: ['./organisation-shipping-address-add.page.scss'],
})
export class OrganisationShippingAddressAddPage implements OnInit {

    @ViewChild('first_name_input', { static: false }) first_name_input: IonInput;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    modal_css_class = '';
    organisation_id = null;
    organisation = null;
    params = {};
    countries = null;
    country = null;
    subdivisions = null;
    address_requires_subdivision = false;
    address_subdivision_label = "State / province / region / county";
    address_requires_postal_code = false;
    address_postal_code_label = "Postcode";

    // modal flags
    country_picker_modal_open = false;

    constructor(
        private modalController: ModalController,
        private alertController: AlertController,
        private loadingController: LoadingController,
        private navParams: NavParams,
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        private observerService: ObserverService,
        private platform: Platform,
        private countryService: CountryService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }

        if(this.navParams.get('organisation_id')) {

            this.organisation_id = this.navParams.get('organisation_id');

            this.params["first_name"] = "";
            this.params["last_name"] = "";
            this.params["organisation_title"] = "";
            this.params["line_1"] = "";
            this.params["line_2"] = "";
            this.params["locality"] = "";
            this.params["subdivision"] = "";
            this.params["postal_code"] = "";
            this.params["country_id"] = "";
            this.params["courier_instructions"] = "";
            this.params["mobile_phone_number"] = "";

            this.loadData().then(() => { }).catch(() => { });

        }

        this.observerService.subscribe('network_status_changed|organisation_shipping_address_add_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => {} ).catch(() => { });
                }
                else {
                    this.presentAlert("Problem", "Offline", "You must be online to add a shipping address, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|organisation_shipping_address_add_modal");
    }

    ionViewDidEnter() {
        // NOTE: we use a timeout here so the DOM gets a chance to load before manipulating elements
        setTimeout(() => {
            if(this.first_name_input) {
                this.first_name_input.setFocus();
            }
        }, 200);
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading organisation").then(() => {
                this.loadOrganisation().then(() => {
                    this.loading_spinner.message = "Loading countries";
                    this.loadCountries().then(() => {
                        this.loading_spinner.dismiss();
                        resolve();
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    loadOrganisation() {
        return new Promise<void>((resolve, reject) => {
            this.organisation = null;
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrganisation(this.organisation_id).then((organisation) => {
                    if(organisation) {
                        this.organisation = organisation;
                    }
                    resolve();
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to retrieve an organisation.");
                    } 
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view an organisation.");
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve an organisation at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    loadCountries() {
        return new Promise<void>((resolve, reject) => {
            this.countries = null;
            this.countryService.getCountries().then((countries) => {
                if(countries) {
                    this.countries = countries;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadCountry(country_id) {
        return new Promise<void>((resolve, reject) => {
            this.country = null;
            this.subdivisions = null;
            this.countryService.getCountry(country_id).then(async (country: any) => {
                this.country = country;
                this.loadCountrySubdivisions(country_id).then((subdivisions) => {
                    this.subdivisions = subdivisions;
                    resolve();
                })
                .catch(() => {
                    reject();
                });
            })
            .catch(() => {
                reject();
            });
        });
    }

    loadCountrySubdivisions(country_id) {
        return new Promise((resolve, reject) => {
            this.subdivisions = null;
            this.countryService.getCountrySubdivisions(country_id).then((subdivisions) => {
                if(subdivisions) {
                    resolve(subdivisions)
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    countryChanged(){

        if(this.params['country_id']) {

            // NOTE: united kingdom not required, address might not have postcode yet
            if(this.country.title == "Australia" || this.country.title == "Canada" || this.country.title == "Ireland" || this.country.title == "Japan" || this.country.title == "Mexico" || this.country.title == "New Zealand" || this.country.title == "United States") {
                this.address_requires_postal_code = true;
            }
            else {
                this.address_requires_postal_code = false;
            }

            if(this.subdivisions && this.subdivisions.length != 0) {
                this.address_requires_subdivision = true;
            }
            else {
                this.address_requires_subdivision = false;
            }

            if(this.country.title == "Australia") {
                this.address_subdivision_label = "State / territory";
            }
            else if(this.country.title == "Canada") {
                this.address_subdivision_label = "Province / territory";
                this.address_postal_code_label = "Postal code";
            }
            else if(this.country.title == "Ireland") {
                this.address_subdivision_label = "County";
            }
            else if(this.country.title == "Japan") {
                this.address_subdivision_label = "Prefecture";
            }
            else if(this.country.title == "Mexico") {
                this.address_subdivision_label = "State";
            }
            else if(this.country.title == "New Zealand") {
                this.address_subdivision_label = "Region";
            }
            else if(this.country.title == "Philippines") {
                this.address_postal_code_label = "ZIP";
            }
            else if(this.country.title == "South Africa") {
                this.address_subdivision_label = "Province";
            }
            else if(this.country.title == "United States") {
                this.address_subdivision_label = "State";
                this.address_postal_code_label = "ZIP";
            }
            else {
                this.address_subdivision_label = "State / province / region / county";
                this.address_postal_code_label = "Postcode";
            }

        }
    }

    close() {
        this.modalController.dismiss();
    }

    async presentCountryPickerModal() {
        if(!this.country_picker_modal_open) {
            this.country_picker_modal_open = true;
            let existing_country_id = "";
            if(this.params['country_id']) {
                existing_country_id = this.params['country_id'];
            }
            const modal = await this.modalController.create({
                component: CountryPickerPage,
                componentProps: {
                    country_id: (this.params['country_id'] ? this.params['country_id'] : "")

                }
            });
            modal.onDidDismiss().then((data) => {
                this.country_picker_modal_open = false;
                if(data.data) {
                    if(data.data == "country_deleted") {
                        delete this.params['country_id'];
                        delete this.params['line_1'];
                        delete this.params['line_2'];
                        delete this.params['locality'];
                        delete this.params['subdivision'];
                        delete this.params['postal_code'];
                        this.country = null;
                    }
                    else {
                        if(data.data['country_id'] != existing_country_id) {
                            this.params["country_id"] = data.data['country_id'];
                            this.loadCountry(data.data['country_id']).then(() => {
                                this.countryChanged();
                            })
                            .catch(() => { });
                        }
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async add() {

        let validated = true;

        if(this.params['first_name'] == null || this.params['first_name'].trim() === "") {
            this.presentAlert("Problem", "Missing first name", "Please enter a first name.");
            validated = false;
        }
        else if(this.params['last_name'] == null || this.params['last_name'].trim() === "") {
            this.presentAlert("Problem", "Missing last name", "Please enter a last name.");
            validated = false;
        }
        else if(this.params['country_id'] == null || this.params['country_id'].trim() === "") {
            this.presentAlert("Problem", "Missing country", "Please select a country.");
            validated = false;
        }
        else {

            if(this.params['line_1'] == null || this.params['line_1'].trim() === "") {
                this.presentAlert("Problem", "Missing line 1", "Please enter a Line 1.");
                validated = false;
            }
            else if(this.params['locality'] == null || this.params['locality'].trim() === "") {
                this.presentAlert("Problem", "Missing town / city", "Please enter a town / city.");
                validated = false;
            }
            else if(this.address_requires_subdivision && (this.params['subdivision'] == null || this.params['subdivision'].trim() === "")) {
                this.presentAlert("Problem", "Missing " + this.address_subdivision_label, "Please enter a " + this.address_subdivision_label + ".");
                validated = false;
            }
            else if(this.address_requires_postal_code && (this.params['postal_code'] == null || this.params['postal_code'].trim() === "")) {
                this.presentAlert("Problem", "Missing " + this.address_postal_code_label, "Please enter a " + this.address_postal_code_label + ".");
                validated = false;
            }
            else {

                await this.loadCountrySubdivisions(this.params['country_id'] ).then((subdivisions: any) => {

                    if(subdivisions && subdivisions.length) {

                        let subdivision_found = false;

                        for (let i = 0; i < subdivisions.length; i++) {

                            let subdivision = subdivisions[i];

                            if(subdivision.title == this.params['subdivision']) {
                                subdivision_found = true;
                                this.params['subdivision'] = subdivision.title;
                                break;
                            }
                            
                        }

                        if(!subdivision_found) {
                            this.presentAlert("Problem", "Invalid " + this.address_subdivision_label, "Please enter a valid " + this.address_subdivision_label + ".");
                            validated = false;
                        }

                    }
                });
            }
        }

        if(validated) {
            this.addOrganisationShippingAddress().then(() => {
                this.modalController.dismiss();
            })
        }

    }

    addOrganisationShippingAddress() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Adding shipping address").then(async () => {
                this.authService.isLoggedIn().then(() => {
                    this.apiService.addOrganisationShippingAddress(this.organisation_id, this.params).then((shipping_address) => {
                        if(shipping_address) {
                            this.observerService.publish('organisation_shipping_address_added', shipping_address);
                        }
                        this.spinner.dismiss();
                        resolve();
                    })
                    .catch((error) => {
                        if(error.status == 400) {
                            this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to add an organisation shipping address.");
                        } 
                        else if(error.status == 403) {
                            this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to add an organisation shipping address.");
                        } 
                        else if(error.status == 404 || error.status == 0) {
                            this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to add an organisation shipping address at this time.");
                        }
                        this.spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                        window.location.reload();
                    });
                });
            });
        });
    }

    async presentAlert(header, sub_header, message) {
        const alert = await this.alertController.create({
            header: header,
            subHeader: sub_header,
            message: message,
            cssClass:'alert',
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}
